/**
 * R15 — promote anonymous global accept-all to server grants on login.
 */
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { CONSENT_VERSION } from '../../shared/consent/constants.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import { notifyConsentSyncFailed } from './consentSyncNotice.js';
import {
  isAcceptAllDecision,
  readEffectiveConsent,
} from './consentPolicy.js';
import { buildConsentPreferencesPayload } from './consentPreferencesPayload.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';
import {
  putPlatformConsentPreferences,
  type ConsentApiRolloutFallback,
} from './platformConsentApi.js';
import { resolveConsentApiFallbackTenantCode } from './resolveConsentApiFallbackTenantCode.js';
import type { CustomerMembershipItemDTO } from '../account/accountTypes.js';

const PENDING_HYDRATE_KEY = 'customer:consent:pendingHydrate:__platform__';

function getDefaultStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function hasPendingConsentHydration(_tenantCode?: string): boolean {
  const storage = getSessionStorage();
  if (storage === null) {
    return false;
  }
  return storage.getItem(PENDING_HYDRATE_KEY) === '1';
}

export function markPendingConsentHydration(_tenantCode?: string): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  storage.setItem(PENDING_HYDRATE_KEY, '1');
}

export function clearPendingConsentHydration(_tenantCode?: string): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  storage.removeItem(PENDING_HYDRATE_KEY);
}

export interface HydrateServerConsentInput {
  readonly tenantCode: string;
  readonly customerId: number;
  readonly authToken: string | null;
  readonly api: ApiClient;
  readonly loyaltyProfilingAvailable?: boolean;
  readonly memberships?: readonly CustomerMembershipItemDTO[] | null;
}

export async function hydrateServerConsentFromClient(
  input: HydrateServerConsentInput,
): Promise<boolean> {
  const storage = getDefaultStorage();
  if (storage === null) {
    return false;
  }

  const storageTenant = resolveConsentStorageTenantCode(input.tenantCode);
  const loyaltyProfilingAvailable = input.loyaltyProfilingAvailable === true;
  const globalRaw = storage.getItem('customer:consent:global:accept-all');
  const shouldRetry = hasPendingConsentHydration();
  if (globalRaw === null && !shouldRetry) {
    return false;
  }

  const decision = readEffectiveConsent({
    tenantCode: storageTenant,
    consentVersion: CONSENT_VERSION,
    loyaltyProfilingAvailable,
    storage,
  });
  if (
    decision === null ||
    !isAcceptAllDecision(decision, { loyaltyProfilingAvailable }) ||
    !decision.analytics ||
    !decision.marketing
  ) {
    clearPendingConsentHydration();
    return false;
  }

  try {
    const { apiBaseUrl } = loadRuntimeConfig();
    const rollout: ConsentApiRolloutFallback = {
      api: input.api,
      fallbackTenantCode: resolveConsentApiFallbackTenantCode(
        input.tenantCode,
        input.memberships,
      ),
    };
    await putPlatformConsentPreferences({
      apiBaseUrl,
      body: buildConsentPreferencesPayload(storageTenant, {
        analytics: decision.analytics,
        marketing: decision.marketing,
      }),
      rollout,
    });

    clearPendingConsentHydration();
    return true;
  } catch (err: unknown) {
    customerLogger.warn('consent promotion hydrate failed', err);
    markPendingConsentHydration();
    notifyConsentSyncFailed();
    return false;
  }
}
